import React, { useState } from 'react';
import ProjectSelector from '../components/ProjectSelector';
import { Project } from '../services/projectService';
import PermissionGuard, { usePermissions } from '../components/PermissionGuard';
import { Permission } from '../services/permissionService';

/**
 * ProjectListPage
 * Main page for Project Owners to select and manage their projects
 * Similar to opscosttrack project management interface
 */
const ProjectListPage: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const { getRoleDisplayName } = usePermissions();

  const handleProjectSelect = (project: Project | null) => {
    setSelectedProject(project);
    
    if (project) {
      // In the future, this will navigate to project detail page or load allocation data
      console.log('Selected project:', project);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Project Management</h1>
              <p className="mt-2 text-gray-600">
                Manage team allocations, budgets, and project costs as {getRoleDisplayName()}
              </p>
            </div>
            <PermissionGuard permission={Permission.VIEW_PROJECT_HEALTH} hideIfNoPermission>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <p className="text-sm text-blue-800 font-medium">
                  📊 BU Head View Active
                </p>
              </div>
            </PermissionGuard>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Project List (Left Side) */}
          <div className="lg:col-span-2">
            <ProjectSelector
              selectedProject={selectedProject}
              onProjectSelect={handleProjectSelect}
              showFilters={true}
            />
          </div>

          {/* Project Details / Actions (Right Side) */}
          <div className="lg:col-span-1">
            {selectedProject ? (
              <div className="bg-white rounded-lg shadow-sm">
                {/* Project Info Card */}
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Project</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Project Name</p>
                      <p className="font-medium text-gray-900">{selectedProject.name}</p>
                    </div>
                    {selectedProject.identifier && (
                      <div>
                        <p className="text-sm text-gray-600">Identifier</p>
                        <p className="font-mono text-sm text-gray-900">{selectedProject.identifier}</p>
                      </div>
                    )}
                    {selectedProject.description && (
                      <div>
                        <p className="text-sm text-gray-600">Description</p>
                        <p className="text-sm text-gray-900">{selectedProject.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Quick Actions</h4>
                  <div className="space-y-3">
                    <PermissionGuard permission={Permission.ADD_FUTURE_ALLOCATIONS}>
                      <button className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Add Team Allocation
                      </button>
                    </PermissionGuard>

                    <PermissionGuard permission={Permission.ENTER_REVENUE}>
                      <button className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Enter Revenue
                      </button>
                    </PermissionGuard>

                    <PermissionGuard permission={Permission.VIEW_COSTS}>
                      <button className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        View Cost Summary
                      </button>
                    </PermissionGuard>

                    <PermissionGuard permission={Permission.ADD_MEMBERS}>
                      <button className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                          />
                        </svg>
                        Manage Team Members
                      </button>
                    </PermissionGuard>
                  </div>
                </div>

                {/* Project Stats Placeholder */}
                <div className="p-6 bg-gray-50 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Project Overview</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Team Members</span>
                      <span className="font-medium text-gray-900">--</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">This Month Budget</span>
                      <span className="font-medium text-gray-900">--</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Allocated %</span>
                      <span className="font-medium text-gray-900">--</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 italic">
                    Select a project to view detailed statistics
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Project Selected</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Select a project from the list to manage team allocations and view details
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Team Allocation</p>
                <p className="text-lg font-semibold text-gray-900">Manage Resources</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Revenue Tracking</p>
                <p className="text-lg font-semibold text-gray-900">Budget Planning</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-lg p-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Cost Analysis</p>
                <p className="text-lg font-semibold text-gray-900">View Reports</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectListPage;


